// deepLinkHelper.js
import { WEB_BASE_URL } from "../Constants/constantValues";

/**
 * Encodes a meeting URL with the scheme "emotii://".
 * It appends action, meeting_id, meeting_title, and meeting_subtitle (optional).
 *
 * @param {number} meeting_id - The ID of the meeting.
 * @param {string} meeting_title - The title of the meeting.
 * @param {string|null} [meeting_subtitle=null] - The subtitle of the meeting (optional).
 * @returns {string} - The generated deep link.
 */
export function encodeMeetingURL(
  meeting_id,
  meeting_title,
  meeting_subtitle = null
) {
  // Create a URLSearchParams object to build query parameters
  const params = new URLSearchParams();

  params.append("action", "meeting");
  params.append("meeting_id", meeting_id);
  params.append("meeting_title", meeting_title);

  if (meeting_subtitle) {
    params.append("meeting_subtitle", meeting_subtitle);
  }

  // Construct the deep link with emotii:// scheme and the parameters
  const deepLink = `emotii://?${params.toString()}`;

  return deepLink;
}

/**
 * Decodes a meeting URL with the scheme "emotii://".
 * It parses the query parameters to extract action, meeting_id, meeting_title, and meeting_subtitle.
 * If meeting_id, meeting_title, and action are not present or action is not "meeting", it returns null.
 *
 * @param {string} deepLink - The deep link URL to decode.
 * @returns {Object|null} - The decoded data as an object or null if validation fails.
 */
export function decodeMeetingURL(deepLink) {
  if (!deepLink) {
    return null;
  }

  // Ensure the deep link has the expected scheme
  if (!deepLink.startsWith("emotii://")) {
    return null; // Invalid deep link scheme
  }

  // Extract the query string from the URL (after "?")
  const queryString = deepLink.split("?")[1];

  // Use URLSearchParams to parse the query string
  const params = new URLSearchParams(queryString);

  // Extract values from the parameters
  const action = params.get("action");
  const meeting_id = parseInt(params.get("meeting_id"), 10);
  const meeting_title = params.get("meeting_title");

  // If action is not "meeting", or any required parameter is missing, return null
  if (action !== "meeting" || isNaN(meeting_id) || !meeting_title) {
    return null;
  }

  const meeting_subtitle = params.get("meeting_subtitle") || null;

  // Return the decoded data as an object
  return {
    action,
    meeting_id,
    meeting_title,
    meeting_subtitle,
  };
}

export function encodeJoinByCodeURL(displayId, passcode) {
  const params = new URLSearchParams();

  params.append("action", "join_by_code");
  params.append("display_id", displayId);
  params.append("passcode", passcode);

  return `emotii://?${params.toString()}`;
}

export function decodeJoinByCodeURL(deepLink) {
  if (!deepLink || !deepLink.startsWith("emotii://")) {
    return null;
  }

  const params = new URLSearchParams(deepLink.split("?")[1]);
  const displayId = params.get("display_id");
  const passcode = params.get("passcode");

  if (params.get("action") !== "join_by_code" || !displayId || !passcode) {
    return null;
  }

  return { displayId, passcode };
}

export function buildMeetingJoinWebLink(joinToken) {
  const params = new URLSearchParams();
  params.append("token", joinToken);

  return `${WEB_BASE_URL}/#/meetings/join?${params.toString()}`;
}

export function isValidMeetingData(data) {
  return (
    data.hasOwnProperty("meeting_id") &&
    Number.isInteger(data.meeting_id) &&
    data.hasOwnProperty("meeting_title") &&
    typeof data.meeting_title === "string"
  );
}
